<template>
  <v-app id="inspire">
    <Sidebar v-model:drawer="drawer" :selected="selectedNav" @toggle-sidebar="EventSidebar"></Sidebar>

    <!-- Top app bar -->
    <v-app-bar flat height="64" color="surface" class="int-appbar">
      <v-app-bar-nav-icon
        class="d-md-none"
        aria-label="Toggle navigation menu"
        @click="drawer = !drawer"
      ></v-app-bar-nav-icon>
      <div class="d-flex align-center px-2">
        <v-icon color="primary" class="mr-2">{{ navIcon }}</v-icon>
        <div class="text-left">
          <div class="text-subtitle-1 font-weight-bold int-page-title">{{ selectedNav }}</div>
          <div class="int-crumbs text-caption d-none d-sm-block">{{ navSubtitle }}</div>
        </div>
      </div>

      <v-spacer></v-spacer>

      <!-- Global search (Ctrl/⌘ + K) -->
      <v-btn variant="tonal" class="mr-2 int-search-btn d-none d-sm-flex" @click="paletteOpen = true">
        <v-icon start>mdi-magnify</v-icon>
        Search
        <span class="int-search-kbd ml-3">⌘K</span>
      </v-btn>
      <v-btn icon variant="text" class="mr-1 d-sm-none" aria-label="Search" @click="paletteOpen = true">
        <v-icon>mdi-magnify</v-icon>
      </v-btn>

      <!-- Notifications -->
      <v-menu
        v-model="bellMenu"
        location="bottom end"
        :close-on-content-click="false"
        @update:modelValue="onBellToggle"
      >
        <template v-slot:activator="{ props }">
          <v-btn icon variant="text" class="mr-1" v-bind="props" aria-label="Notifications">
            <v-badge :model-value="unreadCount > 0" :content="unreadCount" color="error">
              <v-icon>mdi-bell-outline</v-icon>
            </v-badge>
          </v-btn>
        </template>
        <v-card width="360" class="int-menu-card">
          <div class="d-flex align-center justify-space-between px-4 py-3">
            <span class="text-subtitle-2 font-weight-bold">Notifications</span>
            <v-btn
              v-if="notifications.length"
              size="x-small" variant="text" color="primary"
              @click="markAllRead"
            >Mark all read</v-btn>
          </div>
          <v-divider></v-divider>
          <div v-if="notifications.length === 0" class="text-center py-8 px-4">
            <v-icon size="40" color="grey-lighten-1">mdi-bell-check-outline</v-icon>
            <div class="text-body-2 font-weight-medium mt-2">You're all caught up</div>
            <div class="text-caption text-grey">No new notifications</div>
          </div>
          <v-list v-else density="compact" lines="three" class="int-notif-list">
            <v-list-item
              v-for="n in notifications"
              :key="n.id"
              :class="{ 'int-notif-unread': !n.read_at }"
            >
              <template v-slot:prepend>
                <v-avatar size="34" :color="notifColor(n.type)" variant="tonal">
                  <v-icon size="18">{{ notifIcon(n.type) }}</v-icon>
                </v-avatar>
              </template>
              <v-list-item-title class="text-body-2 font-weight-medium text-wrap">{{ n.title }}</v-list-item-title>
              <v-list-item-subtitle class="text-caption text-wrap">{{ n.body }}</v-list-item-subtitle>
              <div class="text-caption text-grey mt-1">
                <span v-if="n.actor_name">{{ n.actor_name }} · </span>{{ timeAgo(n.created_at) }}
              </div>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>

      <!-- Account menu -->
      <v-menu location="bottom end">
        <template v-slot:activator="{ props }">
          <v-btn icon variant="text" class="mr-1" v-bind="props" aria-label="Account menu">
            <v-avatar size="32" color="secondary">
              <span class="text-caption font-weight-bold int-on-dark-accent">{{ initials }}</span>
            </v-avatar>
          </v-btn>
        </template>
        <v-card width="280" class="int-menu-card">
          <div class="d-flex align-center px-4 py-3">
            <v-avatar size="40" color="secondary" class="mr-3">
              <span class="text-subtitle-2 font-weight-bold int-on-dark-accent">{{ initials }}</span>
            </v-avatar>
            <div class="text-left" style="min-width:0">
              <div class="text-subtitle-2 font-weight-bold text-truncate">{{ displayName }}</div>
              <div class="text-caption text-grey text-truncate">{{ email }}</div>
              <v-chip v-if="role" size="x-small" color="primary" variant="tonal" class="mt-1">{{ role }}</v-chip>
            </div>
          </div>
          <v-divider></v-divider>
          <v-list density="compact" nav>
            <v-list-item prepend-icon="mdi-email-clock-outline" title="Task email settings" @click="taskEmailDlg = true"></v-list-item>
            <v-list-item prepend-icon="mdi-cog-outline" title="User Management" @click="EventSidebar('User Management')"></v-list-item>
          </v-list>
          <v-divider></v-divider>
          <div class="pa-2">
            <LogoutDialog @toggle-loading="EventLogout"></LogoutDialog>
          </div>
        </v-card>
      </v-menu>
    </v-app-bar>

    <v-main class="int-main">
      <div class="int-content">
        <Overview v-if="selectedNav == 'Home'"></Overview>
        <ProjectPage v-else-if="selectedNav == 'Projects'"></ProjectPage>
        <TaskPage v-else-if="selectedNav == 'Board'"></TaskPage>
        <AllTicketsPage v-else-if="selectedNav == 'All Project Tickets'"></AllTicketsPage>
        <SprintPage v-else-if="selectedNav == 'Sprints'"></SprintPage>
        <GanttPage v-else-if="selectedNav == 'Project Timeline'"></GanttPage>
        <StandupPage v-else-if="selectedNav == 'Daily Check-In'"></StandupPage>
        <ItSupportPage v-else-if="selectedNav == 'IT Support'"></ItSupportPage>
        <QaTestCasesPage v-else-if="selectedNav == 'QA / Test Cases'"></QaTestCasesPage>
        <TeamPage v-else-if="selectedNav == 'Teams'"></TeamPage>
        <UsersManagementPage v-else></UsersManagementPage>
      </div>
    </v-main>

    <LoadingOverlay :toggle-loader="isloading"></LoadingOverlay>

    <TaskEmailSettings v-model="taskEmailDlg" :employee="selfEmployee" @toggle-message="onToast" @saved="onEmailPrefSaved"></TaskEmailSettings>
    <CommandPalette v-model="paletteOpen"></CommandPalette>
    <v-snackbar v-model="toast.show" :timeout="2200">{{ toast.msg }}</v-snackbar>
  </v-app>
</template>

<script>
import axios from 'axios';
import utils from '@/utils';
import constant from '@/constant';
import Sidebar from '@/components/Sidebar.vue';
import Overview from '@/components/OverviewPage.vue';
import ProjectPage from '@/components/ProjectPage.vue';
import TaskPage from '@/components/TaskPage.vue';
import AllTicketsPage from '@/components/AllTicketsPage.vue';
import SprintPage from '@/components/SprintPage.vue';
import GanttPage from '@/components/GanttPage.vue';
import StandupPage from '@/components/StandupPage.vue';
import ItSupportPage from '@/components/ItSupportPage.vue';
import QaTestCasesPage from '@/components/QaTestCasesPage.vue';
import TeamPage from '@/components/TeamPage.vue';
import UsersManagementPage from '@/components/UsersManagementPage.vue';
import LogoutDialog from '@/components/LogoutDialog.vue';
import LoadingOverlay from '@/components/LoadingOverlay.vue';
import TaskEmailSettings from '@/components/TaskEmailSettings.vue';
import CommandPalette from '@/components/CommandPalette.vue';

export default {
  name: "DashboardView",
  components: {
    Sidebar,
    Overview,
    ProjectPage,
    TaskPage,
    AllTicketsPage,
    SprintPage,
    GanttPage,
    StandupPage,
    ItSupportPage,
    QaTestCasesPage,
    TeamPage,
    UsersManagementPage,
    LogoutDialog,
    LoadingOverlay,
    TaskEmailSettings,
    CommandPalette,
  },
  data: () => ({
    constant: constant,
    selectedNav: "Home",
    drawer: true,
    isloading: false,
    user_data: null,
    notifications: [],
    unreadCount: 0,
    bellMenu: false,
    pollTimer: null,
    taskEmailDlg: false,
    paletteOpen: false,
    toast: { show: false, msg: '' },
    navIcons: {
      'Home': 'mdi-view-dashboard-outline',
      'Projects': 'mdi-folder-multiple-outline',
      'Board': 'mdi-view-column-outline',
      'All Project Tickets': 'mdi-ticket-outline',
      'Sprints': 'mdi-run-fast',
      'Project Timeline': 'mdi-chart-timeline',
      'Daily Check-In': 'mdi-account-voice',
      'IT Support': 'mdi-lifebuoy',
      'QA / Test Cases': 'mdi-test-tube',
      'Teams': 'mdi-account-group-outline',
      'User Management': 'mdi-account-cog-outline',
    },
  }),
  computed: {
    navIcon() {
      return this.navIcons[this.selectedNav] || 'mdi-view-dashboard-outline';
    },
    // Per-page description shown under the title in the top bar.
    navSubtitle() {
      const map = {
        'Home': 'Your cross-module overview and today\'s work.',
        'Board': 'Drag tickets across columns to update their status.',
        'All Project Tickets': 'Browse and filter every ticket across projects and sprints.',
        'Projects': 'Create and manage projects, members, and timelines.',
        'Sprints': 'Plan sprints and pull items in from the backlog.',
        'Project Timeline': 'Gantt view of sprints and tickets against the project schedule.',
        'Daily Check-In': 'Post your update and see who has checked in.',
        'IT Support': 'Log and track internal IT support tickets.',
        'QA / Test Cases': 'Manage test cases and record executions.',
        'Teams': 'Manage teams and their members.',
        'User Management': 'Manage employees, roles, and access.',
      };
      return map[this.selectedNav] || `Dashboard / ${this.selectedNav}`;
    },
    displayName() {
      const u = this.user_data;
      if (u && u.employee_information && u.employee_information.fullname) {
        return u.employee_information.fullname;
      }
      return (u && u.email) ? u.email.split('@')[0] : 'User';
    },
    email() {
      return (this.user_data && this.user_data.email) || '';
    },
    role() {
      const u = this.user_data;
      if (!u) return '';
      return u.role || (u.employee_information && u.employee_information.role) || '';
    },
    initials() {
      const name = this.displayName || 'U';
      const parts = name.trim().split(/\s+/);
      const a = parts[0] ? parts[0][0] : '';
      const b = parts.length > 1 ? parts[parts.length - 1][0] : '';
      return (a + b).toUpperCase() || 'U';
    },
    // The logged-in user's employee record, for their own task-email settings.
    selfEmployee() {
      const u = this.user_data || {};
      return u.employee_information || null;
    },
  },
  methods: {
    authHeaders() {
      return {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      };
    },
    EventSidebar(value) {
      this.selectedNav = value;
    },
    onToast(msg) {
      this.toast = { show: true, msg };
    },
    // Keep the cached user_data in sync so re-opening the dialog shows the
    // saved values without a re-login.
    onEmailPrefSaved(payload) {
      if (this.user_data && this.user_data.employee_information) {
        this.user_data.employee_information.email_daily_tasks = payload.email_daily_tasks;
        this.user_data.employee_information.daily_tasks_time = payload.daily_tasks_time;
        try { localStorage.setItem('user_data', JSON.stringify(this.user_data)); } catch (e) { /* ignore */ }
      }
    },
    // Allow deeply-nested components (e.g. ViewTicket) to switch the active page
    // via a window event instead of prop drilling.
    onNavigate(e) {
      const nav = e && e.detail && e.detail.nav;
      if (nav) this.selectedNav = nav;
    },
    // Ctrl/⌘ + K opens the global command palette.
    onKeydown(e) {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        this.paletteOpen = true;
      }
    },
    fetchNotifications() {
      if (!localStorage.getItem('token')) return;
      axios.get(utils._api(this.constant.list_notifications), this.authHeaders())
        .then((response) => {
          this.notifications = response.data.data || [];
          this.unreadCount = response.data.unread_count || 0;
        })
        .catch((error) => console.error('Error fetching notifications:', error));
    },
    onBellToggle(open) {
      // When the panel is opened, clear the unread badge.
      if (open && this.unreadCount > 0) {
        this.markAllRead();
      }
    },
    markAllRead() {
      axios.post(utils._api(this.constant.mark_all_notifications_read), {}, this.authHeaders())
        .then(() => {
          this.unreadCount = 0;
          this.notifications = this.notifications.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() }));
        })
        .catch((error) => console.error('Error marking notifications read:', error));
    },
    notifIcon(type) {
      const map = {
        ticket_assigned: 'mdi-ticket-confirmation-outline',
        ticket_status: 'mdi-swap-horizontal',
        ticket_comment: 'mdi-comment-text-outline',
        sprint_started: 'mdi-run-fast',
        sprint_completed: 'mdi-flag-checkered',
        standup_reminder: 'mdi-account-voice',
        standup_blocker: 'mdi-alert-octagon-outline',
      };
      return map[type] || 'mdi-bell-outline';
    },
    notifColor(type) {
      const map = {
        ticket_assigned: 'info',
        ticket_status: 'primary',
        ticket_comment: 'secondary',
        sprint_started: 'success',
        sprint_completed: 'warning',
        standup_reminder: 'info',
        standup_blocker: 'error',
      };
      return map[type] || 'primary';
    },
    timeAgo(dateStr) {
      if (!dateStr) return '';
      const then = new Date(dateStr).getTime();
      if (isNaN(then)) return '';
      const diff = Math.floor((Date.now() - then) / 1000);
      if (diff < 60) return 'just now';
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
      return new Date(dateStr).toLocaleDateString();
    },
    EventLogout(value) {
      this.isloading = true;
      if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
      setTimeout(() => {
        this.$router.push({ name: 'login' });
        localStorage.removeItem('token');
        localStorage.removeItem('user_data');
      }, 4000);
      console.log("from logout ", value);
    }
  },
  created() {
    try {
      const raw = localStorage.getItem('user_data');
      this.user_data = raw ? JSON.parse(raw) : null;
    } catch (e) {
      this.user_data = null;
    }
    this.fetchNotifications();
    // Poll so new movements show up without a refresh.
    this.pollTimer = setInterval(this.fetchNotifications, 30000);
    window.addEventListener('int:navigate', this.onNavigate);
    window.addEventListener('keydown', this.onKeydown);
  },
  beforeUnmount() {
    if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
    window.removeEventListener('int:navigate', this.onNavigate);
    window.removeEventListener('keydown', this.onKeydown);
  }
}
</script>

<style scoped>
.int-appbar {
  border-bottom: 1px solid var(--int-border);
}
.int-page-title {
  line-height: 1.1;
}
.int-crumbs {
  color: #9a9b9f;
  line-height: 1.1;
}
.int-main {
  background: var(--int-grey-bg);
}
.int-content {
  max-width: 100%;
  margin: 0;
  padding: 0;
}
/* Brand-yellow monogram on the dark avatar. */
.int-on-dark-accent {
  color: var(--int-yellow);
}
.int-logout {
  display: flex;
  align-items: center;
  color: #6b6d75;
}
.int-notif-list {
  max-height: 380px;
  overflow-y: auto;
}
.int-notif-unread {
  background: rgba(253, 204, 14, 0.08);
}
.int-menu-card {
  border: 1px solid var(--int-border);
}
/* Topbar global-search trigger, reads like a search box. */
.int-search-btn {
  text-transform: none;
  font-weight: 500;
  color: var(--int-text-muted);
  background: var(--int-surface-muted);
  border: 1px solid var(--int-border);
}
.int-search-kbd {
  font-size: 0.66rem;
  font-weight: 700;
  color: var(--int-text-faint);
  border: 1px solid var(--int-border);
  border-radius: 4px;
  padding: 0 5px;
  background: #fff;
}
</style>
